import { useEffect } from 'react';
import { type ResolvedTheme } from '@/lib/platform/resolve-shell-theme';
import { events } from '@/lib/platform/generated/bindings.gen';

/**
 * Live cross-window theme sync for desktop.
 *
 * Each Tauri WebviewWindow (main, recording_hud, meeting_detected_overlay) is an
 * isolated webview with its own localStorage — they cannot observe a
 * localStorage-based theme change in another window. Instead, the `set_theme`
 * Tauri command persists the choice and emits a native `theme-changed` event to
 * ALL windows. This hook listens for that event and re-applies the resolved
 * `data-theme`, so every already-open window updates live.
 *
 * Mount once per window. The window that triggered the change also receives the
 * broadcast (Tauri's `app.emit` includes the emitter), but re-applying the same
 * `data-theme` is an idempotent DOM write — no state, no thrash, no loop.
 */

export type { ResolvedTheme };

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Maps a raw stored theme payload (`"light"`, `"dark"`, `"system"`, or any
 * unrecognised value) to a resolved light/dark value. `"system"` and unknown
 * payloads resolve via matchMedia — mirroring launch-time resolution so a live
 * change lands identically to a fresh window boot.
 */
export function resolveThemePayload(payload: string | null | undefined): ResolvedTheme {
  if (payload === 'light' || payload === 'dark') return payload;
  return getSystemTheme();
}

function applyResolved(resolved: ResolvedTheme): void {
  if (resolved === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

export function useThemeSync(): void {
  useEffect(() => {
    let unlisten: (() => void) | undefined;
    let cancelled = false;

    events.themeChanged.listen((event) => {
          applyResolved(resolveThemePayload(event.payload));
        })
      .then((fn) => {
        if (cancelled) {
          fn();
        } else {
          unlisten = fn;
        }
      })
      .catch(() => {
        // Non-Tauri environment (web) or listener setup failed — no-op.
      });

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);
}
